import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Typography,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Divider
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Event as EventIcon,
  MusicNote as MusicIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ArtistAnalyticsPage = () => {
  const { user } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    venueType: '',
    dateFrom: null,
    dateTo: null
  });

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        sortBy: 'playedAt',
        sortOrder: 'desc'
      });

      // Add filters to params
      if (filters.venueType) params.append('venueType', filters.venueType);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());

      const response = await axios.get(`${API_URL}/play-logs/analytics?${params}`);
      const { playLogs, pagination, stats } = response.data;
      
      setAnalytics(playLogs);
      setTotalPages(pagination.totalPages);
      setStats(stats);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    if (user && user.accountType === 'artist') {
      fetchAnalytics();
    }
  }, [user, fetchAnalytics]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  if (user?.accountType !== 'artist') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          This page is only accessible to artist accounts.
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#e4e4e4' }}>
            Play Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            See where and when your music has been played by curators
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.totalPlays || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Plays
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.uniqueCurators || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Curators
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {stats.uniqueVenues || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Venues
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {stats.venueTypes?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Venue Types
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Venue Types Overview */}
        {stats.venueTypes && stats.venueTypes.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Venue Types
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {stats.venueTypes.map((type) => (
                <Chip
                  key={type}
                  label={type.replace('_', ' ').toUpperCase()}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}>
                <InputLabel>Venue Type</InputLabel>
                <Select
                  value={filters.venueType}
                  label="Venue Type"
                  onChange={(e) => handleFilterChange('venueType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="club">Club</MenuItem>
                  <MenuItem value="festival">Festival</MenuItem>
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="podcast">Podcast</MenuItem>
                  <MenuItem value="livestream">Livestream</MenuItem>
                  <MenuItem value="private_event">Private Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="From Date"
                value={filters.dateFrom}
                onChange={(date) => handleFilterChange('dateFrom', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="To Date"
                value={filters.dateTo}
                onChange={(date) => handleFilterChange('dateTo', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Analytics Results */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : analytics.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No play data available yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When curators play your music, the analytics will appear here
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {analytics.map((log) => (
              <Card key={log._id}>
                <CardContent>
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Curator Info */}
                    <Grid item xs={12} md={2.4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography 
                            variant="body2"
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '180px'
                            }}
                            title={log.curator.artistName || log.curator.username}
                          >
                            {log.curator.artistName || 'Unknown Artist'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Release/Track Info */}
                    <Grid item xs={12} md={2.4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MusicIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="bold">
                            {log.release.title}
                          </Typography>
                        </Box>
                        {log.track && (
                          <Typography variant="body2" color="primary" sx={{ ml: 3 }}>
                            {log.track.title}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Date */}
                    <Grid item xs={12} md={2.4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EventIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {new Date(log.playedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {new Date(log.playedAt).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} md={2.4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {log.venue.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {log.venue.location?.city && `${log.venue.location.city}, `}
                          {log.venue.location?.country}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12} md={2.4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
                        <Chip
                          label={log.venue.type}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={log.audienceReaction}
                          size="small"
                          color={log.audienceReaction === 'excellent' ? 'success' : 'default'}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {(log.event?.name || log.notes) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        {log.event?.name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Event:</strong> {log.event.name}
                            </Typography>
                          </Grid>
                        )}
                        {log.event?.attendees && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Attendees:</strong> {log.event.attendees}
                            </Typography>
                          </Grid>
                        )}
                        {log.notes && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Notes:</strong> {log.notes}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ArtistAnalyticsPage;
